import React, { useMemo } from 'react';
import Select from 'react-select';

/**
 * DestinatarioSelect — il campo "A chi" di compiti e promemoria.
 *
 * Tendina E ricerca insieme: con quaranta persone in elenco, scorrere una
 * tendina e' lento, ma toglierla del tutto costringe a ricordarsi i nomi.
 * Cosi' si apre e si scorre come prima, oppure si digita cognome o nome e
 * l'elenco si restringe.
 *
 * Le due schermate codificano il destinatario in modo diverso (una usa
 * 'u:12' e 'r:medico', l'altra 'u12' e 'medico'): il formato lo decide chi
 * chiama con `prefisso`, cosi' il componente resta uno solo e nessuna delle
 * due schermate deve cambiare il proprio salvataggio.
 *
 * Props:
 *  - value        stringa del destinatario, nel formato scelto
 *  - onChange     (nuovoValore) => void
 *  - ruoli        [{ valore, etichetta }]
 *  - utenti       elenco utenti (si filtrano gli inattivi)
 *  - prefisso     'due-punti' -> 'r:medico' / 'u:12'   (Promemoria)
 *                 'compatto'  -> 'medico'   / 'u12'    (Compiti)
 *  - vuoto        etichetta dell'opzione senza destinatario; se assente,
 *                 l'opzione non c'e' (i compiti ne richiedono sempre uno)
 *  - disabled
 */
const DestinatarioSelect = ({
    value,
    onChange,
    ruoli = [],
    utenti = [],
    prefisso = 'due-punti',
    vuoto = null,
    disabled = false,
    inputId,
}) => {
    const codifica = useMemo(() => ({
        ruolo: (r) => (prefisso === 'compatto' ? r : `r:${r}`),
        utente: (id) => (prefisso === 'compatto' ? `u${id}` : `u:${id}`),
    }), [prefisso]);

    const gruppi = useMemo(() => {
        const g = [];
        if (ruoli.length) {
            g.push({
                label: 'Gruppi',
                options: ruoli.map((r) => ({
                    value: codifica.ruolo(r.valore),
                    label: r.etichetta,
                    cerca: r.etichetta,
                })),
            });
        }
        const attivi = utenti.filter((u) => u.attivo !== false);
        if (attivi.length) {
            g.push({
                label: 'Persone',
                options: attivi.map((u) => ({
                    value: codifica.utente(u.id),
                    label: `${u.cognome} ${u.nome}`,
                    // Si cerca anche per ruolo e per nome scritto al contrario:
                    // chi digita "mario rossi" deve trovare "Rossi Mario".
                    cerca: `${u.cognome} ${u.nome} ${u.nome} ${u.cognome} ${u.ruolo || ''}`,
                })),
            });
        }
        return g;
    }, [ruoli, utenti, codifica]);

    const opzioni = useMemo(() => (
        vuoto ? [{ value: '', label: vuoto, cerca: vuoto }, ...gruppi] : gruppi
    ), [gruppi, vuoto]);

    const selezionata = useMemo(() => {
        const piatte = opzioni.flatMap((o) => (o.options ? o.options : [o]));
        return piatte.find((o) => o.value === (value ?? '')) || null;
    }, [opzioni, value]);

    /* Ricerca per sottostringa su cognome, nome e ruolo: react-select di suo
       guarda solo l'etichetta. */
    const filtra = (option, input) => {
        if (!input) return true;
        const termine = input.toLowerCase().trim();
        return (option.data.cerca || option.label || '')
            .toLowerCase()
            .includes(termine);
    };

    const stili = {
        control: (base, state) => ({
            ...base,
            minHeight: '40px',
            borderRadius: '8px',
            borderColor: state.isFocused ? '#6f5494' : '#d9d5e0',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(111, 84, 148, .12)' : 'none',
            '&:hover': { borderColor: state.isFocused ? '#6f5494' : '#b9b2c6' },
        }),
        placeholder: (base) => ({ ...base, color: '#9a93a6', fontSize: '.92rem' }),
        option: (base, state) => ({
            ...base,
            fontSize: '.92rem',
            backgroundColor: state.isSelected ? '#6f5494'
                : state.isFocused ? '#f3eefa' : 'white',
            color: state.isSelected ? 'white' : '#241f2b',
            cursor: 'pointer',
            padding: '8px 12px',
        }),
        menu: (base) => ({ ...base, borderRadius: '8px', zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    return (
        <Select
            inputId={inputId}
            value={selezionata}
            onChange={(o) => onChange(o ? o.value : '')}
            options={opzioni}
            filterOption={filtra}
            isDisabled={disabled}
            isSearchable
            placeholder="Scegli o cerca per cognome…"
            noOptionsMessage={() => 'Nessuno corrisponde'}
            // Il campo vive dentro una finestra: senza portale il menu
            // resterebbe tagliato dal bordo della modale.
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={stili}
        />
    );
};

export default DestinatarioSelect;
